import {useEffect, useRef} from 'react';
import {useInView} from 'react-intersection-observer';

import EppoFlags from '@/EppoFlags';
import {useStringAssignmentExperimentDetails} from '@/eppoHooks';
import useMediaQuery from '@/hooks/useMediaQuery';
import {appBarMedia} from '@/styles/appBarMq';

type UseEventBarRedesignOptions = {
  trackExposure?: boolean;
};

/**
 * ezx560::Search::EventBarRedesign — arm gate plus opt-in exposure.
 *
 * `seenRef` goes on AppBar's sentinel, never on an arm's own markup: an element only one arm mounts
 * (or mounts later, or only after a click) skews exposure toward the other arm.
 */
const useEventBarRedesign = ({trackExposure = false}: UseEventBarRedesignOptions = {}) => {
  const {variation, trackExperimentExposed} = useStringAssignmentExperimentDetails(
    EppoFlags.EventBarRedesign,
  );
  const isAppBarXL = useMediaQuery(`${appBarMedia.xl}`);

  const {ref: seenRef, inView} = useInView({threshold: 0, triggerOnce: true});
  const hasTrackedExposure = useRef(false);

  useEffect(() => {
    if (!trackExposure || hasTrackedExposure.current) return;
    if (!isAppBarXL || !inView || variation === 'fallback') return;

    // `triggerOnce` pins `inView` true, so a later `subject` change would otherwise re-fire this.
    hasTrackedExposure.current = true;
    trackExperimentExposed();
  }, [trackExposure, isAppBarXL, inView, variation, trackExperimentExposed]);

  const showEventBarRedesign = variation === 'treatment';

  return {showEventBarRedesign, variation, seenRef, inView, isAppBarXL};
};

export default useEventBarRedesign;
